import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuditListResponse } from "@opus-dt/shared-types";
import { api } from "@/lib/api-client";
import { isDraftEntityId } from "@/features/lines/canvas/draft-canvas-adapter";
import type { AlarmSummary } from "@/features/alarms/hooks";
import type {
  StopReason,
  MachineEventSummary,
  AuditEntry,
} from "../../types/machine-sidebar.types.js";
import type { MachineEventReason } from "../../types/machine-event-reasons.types.js";

export type {
  StopReason,
  MachineEventSummary,
  AuditEntry,
} from "../../types/machine-sidebar.types.js";

export function useStopReasons() {
  return useQuery<StopReason[]>({
    queryKey: ["stop-reasons"],
    queryFn: () => api.get<StopReason[]>("/api/v1/stop-reasons"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMachineEventReasons() {
  return useQuery<MachineEventReason[]>({
    queryKey: ["machine-event-reasons"],
    queryFn: () => api.get<MachineEventReason[]>("/api/v1/machine-event-reasons?isActive=true"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMachineEvents(machineId: string, limit = 10) {
  return useQuery<MachineEventSummary[]>({
    queryKey: ["machine-events", machineId, limit],
    queryFn: () =>
      api.get<MachineEventSummary[]>(
        `/api/v1/machine-events?machineId=${machineId}&limit=${limit}`
      ),
    enabled: !!machineId && !isDraftEntityId(machineId),
    refetchInterval: 30_000,
  });
}

export function useMachineAlarms(machineId: string) {
  return useQuery<AlarmSummary[]>({
    queryKey: ["machine-alarms", machineId],
    queryFn: () =>
      api
        .get<AlarmSummary[]>(`/api/v1/alarms?includeSuppressed=true`)
        .then((all) => all.filter((a) => a.machineId === machineId)),
    enabled: !!machineId && !isDraftEntityId(machineId),
    refetchInterval: 30_000,
  });
}

interface JustifyEventPayload {
  reasonSlug: string;
  reasonOtherDesc?: string;
}

export function useJustifyEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: JustifyEventPayload }) =>
      api.patch<MachineEventSummary>(`/api/v1/machine-events/${eventId}/justify`, data),
    meta: { skipErrorToast: true },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["machine-events"] });
      void qc.invalidateQueries({ queryKey: ["lines"] });
      if (data?.machineId) {
        void qc.invalidateQueries({ queryKey: ["machine", data.machineId] });
      }
      void qc.invalidateQueries({ queryKey: ["events"] });
      void qc.invalidateQueries({ queryKey: ["oee"] });
      void qc.invalidateQueries({ queryKey: ["attention"] });
    },
  });
}

export function useAcknowledgeMachineEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) =>
      api.post<MachineEventSummary>(`/api/v1/machine-events/${eventId}/acknowledge`, {}),
    meta: { skipErrorToast: true },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["machine-events"] });
      if (data?.machineId) {
        void qc.invalidateQueries({ queryKey: ["machine", data.machineId] });
      }
      void qc.invalidateQueries({ queryKey: ["events"] });
      void qc.invalidateQueries({ queryKey: ["attention"] });
    },
  });
}

export function useRegisterStatusEntry(machineId: string, lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { value: string; occurredAt: string; observation?: string }) =>
      api.post<{
        machineEventId: string | null;
        machineStatus: "RUNNING" | "STOPPED" | "IDLE" | "NODATA";
        occurredAt: string;
        productionRunId: string | null;
        noop: boolean;
      }>(`/api/v1/machines/${machineId}/status-entry`, body),
    meta: { skipErrorToast: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["machine", machineId] });
      void qc.invalidateQueries({ queryKey: ["machines", lineId] });
      void qc.invalidateQueries({ queryKey: ["lines", lineId] });
      void qc.invalidateQueries({ queryKey: ["events", lineId] });
      void qc.invalidateQueries({ queryKey: ["oee", lineId] });
    },
  });
}

export function useAuditHistory(params: { entityType: string; entityId: string; limit?: number }) {
  const { entityType, entityId, limit = 50 } = params;
  return useQuery<AuditListResponse, Error, AuditEntry[]>({
    queryKey: ["audit-history", entityType, entityId, limit],
    queryFn: () =>
      api.get<AuditListResponse>(
        `/api/v1/audit?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&limit=${limit}`
      ),
    // paginação fica em data (AuditListResponse); o consumidor só precisa das entries
    select: (d) => d.items,
    enabled: !!entityId && !isDraftEntityId(entityId),
    staleTime: 30_000,
  });
}
